// frontend/src/components/purchase-orders/poActions.js
//
// Which actions a purchase order offers in its current state — the one home
// for that rule on the SaaS surface. The PO details page renders the answer
// as one primary button, a [Download ▼] menu and a "More actions" menu; the
// PO list renders it as the primary button and one ⋯ menu per row. Nothing
// else decides per-status availability, so a gate can only drift here.
//
// Each gate mirrors the backend's refusal (purchase_orders/po_viewset.py):
//   issue          draft only (`issue` refuses an issued PO), never ERP-owned
//                  (`_reject_if_erp_managed`).
//   edit           draft only (`update_draft` refuses `issued_at IS NOT NULL`).
//   receive        `is_outstanding` — issued, alive, something still owed:
//                  open / partially_received (`receive_goods`).
//   cancel         open only: `cancel` refuses drafts, any receipt
//                  (po_has_receipts_use_close_incomplete) and terminal states.
//   close          partially_received only: `close` refuses a PO with no
//                  receipts (po_no_receipts_use_cancel) — nothing came, so
//                  the order is voided with Cancel, not kept on record as a
//                  short shipment — and a fully received PO
//                  (po_already_received), which has nothing to write off.
//                  Receipts decide the verb: Cancel and Close incomplete are
//                  never offered together.
//   delete         draft only: `destroy` refuses an issued PO
//                  (po_issued_cannot_delete). Shopify will not delete the
//                  in-transit shipment that mirrors it, so an issued PO is
//                  cancelled, not erased.
//   downloadPdf    every status (a draft prints watermarked DRAFT —
//                  supplier_documents.py), never ERP-owned (`download_pdf`
//                  goes through `_reject_if_erp_managed`).
//   downloadCsv /  every status — the items export is data, not the document
//   downloadXlsx   of record, so the ERP owning the PO does not withhold it.
//   duplicate      every status (`duplicate` makes a NEW draft of the same
//                  items and quantities; the source is not touched). It is
//                  the primary on a received / closed / cancelled PO — the
//                  one thing left to do with a finished order is order it
//                  again — and sits in More actions while the order is
//                  still in flight. Never ERP-owned (`_reject_if_erp_managed`)
//                  and never on a store with an active ERP, where no Manexys
//                  draft exists (`create` refuses the draft stage) and new
//                  orders originate in the ERP, like the list's Create button.
//
// Moving the expected delivery date (`canReschedule`) follows `reschedule`:
// open / partially_received only (`po_not_outstanding` otherwise — a draft
// stores no date and is dated when issued), never ERP-owned.
//
// ERP-owned (`is_erp_managed`, a PO carrying an exact_po_id): always issued —
// a PO gains its exact_po_id only as an issued order — so issue, edit and
// delete never apply. PDF and duplicate are the ERP's; receive, cancel and
// close are offered here and written through to the ERP (receipt pushed,
// order deleted, order reduced to what was received), so both systems show
// one order.

export const PO_ACTION = Object.freeze({
  ISSUE: 'issue',
  EDIT: 'edit',
  RECEIVE: 'receive',
  CANCEL: 'cancel',
  CLOSE: 'close',
  DELETE: 'delete',
  DOWNLOAD_PDF: 'downloadPdf',
  DOWNLOAD_CSV: 'downloadCsv',
  DOWNLOAD_XLSX: 'downloadXlsx',
  DUPLICATE: 'duplicate',
});

// Every action `purchaseOrderActions` can return as `primary`. The list's
// next-step button is as wide as the widest of these, so the buttons line up
// down the column in every language (PurchaseOrderRowActions).
export const PRIMARY_ACTIONS = Object.freeze([PO_ACTION.ISSUE, PO_ACTION.RECEIVE, PO_ACTION.DUPLICATE]);

const EXPORTS = [PO_ACTION.DOWNLOAD_CSV, PO_ACTION.DOWNLOAD_XLSX];

/** Whether the PO's expected delivery date can be moved (see the header). */
export const canReschedule = (po) =>
  (po?.status === 'open' || po?.status === 'partially_received') && !po?.is_erp_managed;

/**
 * @param {{ status: string, is_erp_managed: boolean }} po  PO header (list or
 *   detail wire shape).
 * @param {{ mayCreateOrders: boolean }} store  `mayCreateOrders` once the
 *   store is known to create its new orders in Manexys (useWriteGate
 *   'po_final'); false while an ERP owns them or the answer is not in.
 * @returns {{ primary: string|null, downloads: string[], more: string[] }}
 *   `primary` is the one action the state is for; `downloads` are the files
 *   this PO can produce, which every status offers and no state changes;
 *   `more` is the rest of the lifecycle, and is empty on a state that has
 *   nothing left to do.
 */
export function purchaseOrderActions(po, { mayCreateOrders }) {
  // Required, not defaulted: a forgotten flag must not offer a Duplicate the
  // server refuses.
  if (typeof mayCreateOrders !== 'boolean') {
    throw new Error('purchaseOrderActions: mayCreateOrders is required');
  }
  const erp = !!po.is_erp_managed;
  // The printed document is the ERP's when the ERP owns the order; the items
  // export is data, and stays ours either way.
  const downloads = erp ? EXPORTS : [PO_ACTION.DOWNLOAD_PDF, ...EXPORTS];
  const canDuplicate = !erp && mayCreateOrders;
  const andDuplicate = (more) => (canDuplicate ? [...more, PO_ACTION.DUPLICATE] : more);

  switch (po.status) {
    case 'draft':
      return { primary: PO_ACTION.ISSUE, downloads, more: andDuplicate([PO_ACTION.EDIT, PO_ACTION.DELETE]) };
    case 'open':
      return { primary: PO_ACTION.RECEIVE, downloads, more: andDuplicate([PO_ACTION.CANCEL]) };
    case 'partially_received':
      return { primary: PO_ACTION.RECEIVE, downloads, more: andDuplicate([PO_ACTION.CLOSE]) };
    case 'received':
    case 'closed':
    case 'cancelled':
      // Finished: ordering it again is the one thing left, so it leads.
      return { primary: canDuplicate ? PO_ACTION.DUPLICATE : null, downloads, more: [] };
    default:
      // A status this surface has no rules for must not render as "no
      // actions" — that would read as a terminal PO.
      throw new Error(`purchaseOrderActions: unknown status "${po.status}"`);
  }
}
